import React from 'react';
import { Text } from '@chakra-ui/react';
import { CopyBlock, GuideLayout, GuideTitle, Section, Steps } from './GuideLayout';

export default function GoogleAdsGuidePage() {
  return (
    <GuideLayout
      title="Google Ads for wash and fold pickup"
      description="How a gig worker creates one Google Search campaign for wash and fold pickup: keywords, negatives, the ad, the booking page, and a daily budget."
      path="/slb/get-customers/google-ads"
    >
      <GuideTitle
        kicker="Paid. After the page is ready."
        heading="One Google Search campaign."
        lede="People typing “wash and fold near me” or “laundry pickup in [your city]” already want the service. That search is the campaign. Display ads, YouTube, and a campaign that spends across every Google surface come later, after this search has booked a weekly customer."
      />

      <Section title="Before you open an ad account">
        <Text color="gray.700">
          The ad link is your booking page or your city page, the one titled “Wash and Fold Pickup in [City].” It is not smartlaundrybasket.ai. The page needs a price, a minimum, a phone you answer, and ZIP codes you drive. If those are empty, fix them on the Free SEO page first. A click to a blank page is a paid miss.
        </Text>
      </Section>

      <Section title="Create the campaign">
        <Steps items={[
          ['Start at ads.google.com', 'Use a Google account you can log into next month. Skip the “smart” campaign that writes itself. Create a campaign with the goal of traffic to your website, or sales if it asks where the order happens. The type is Search.'],
          ['Leave the other networks off', 'Uncheck Display Network. Do not choose Performance Max, Demand Gen, or Video for this first campaign. Those spend on places where nobody typed “laundry pickup.”'],
          ['Locations are where you drive', 'Enter the cities or ZIP codes from Service Area. Open location options and choose people in those places, or regularly in them. The other option also shows the ad to someone across the country who once read about your city. Turn that off.'],
          ['One ad group', 'Name it Wash and fold pickup. Put the keywords below in that group with quotes around each line. Quotes mean phrase match: the search has to carry that meaning, and you still see the real wording later. You do not need a separate campaign per neighborhood until one city is booking.'],
          ['Write one ad that matches the page', 'Use the lines below. Replace the city, the price, and the turnaround with the same words on the city page. The final URL is that city page. Add your phone if the ad offers a call.'],
          ['Budget', 'Start at $10 to $15 a day. These clicks are often a few dollars each, so this budget is a few real searches a day, not a flood. Leave it for seven days. Raise it only after a search turns into a booked pickup. If a week of clicks never books, the page or the miles are the problem, not the budget.'],
          ['Count an order, not a click', 'In Google Ads, a conversion is someone reaching your booking page and placing an order. Clicks alone will tell you the ad ran. They will not tell you it worked.'],
        ]} />
      </Section>

      <CopyBlock label="Keywords. Replace CITY with the city you serve, keep the quotes, then paste.">
{`"wash and fold near me"
"laundry pickup and delivery"
"laundry pickup CITY"
"laundry delivery CITY"
"wash and fold CITY"
"fluff and fold CITY"
"wash and fold service CITY"
"laundry service near me"`}
      </CopyBlock>

      <CopyBlock label="Negative keywords. Paste these so you do not pay for jobs, used machines, or a class on how to start.">
{`jobs
hiring
career
salary
for sale
equipment
parts
commercial washer
washer for sale
dryer for sale
how to start
course
diy
laundromat for sale
wholesale
repair`}
      </CopyBlock>

      <CopyBlock label="Ad. Keep it this plain.">
{`Wash and Fold Pickup in CITY
$PRICE/lb · MINIMUM minimum
Book a Pickup on Your Phone
We pick up, wash, fold, and bring it back in TURNAROUND. Serving ZIPS. Book in a minute.`}
      </CopyBlock>

      <Section title="What to check after three days">
        <Steps items={[
          ['Search terms', 'Open the list of real searches that triggered the ad. Add anything that is a job, a machine, or another city to the negative list.'],
          ['The page they landed on', 'If the ad says $1.75 and the page does not, fix the page. The ad and the city page have to agree.'],
          ['The hours', 'Pause the ad on days you cannot pick up. A booked pickup you cannot drive is worse than no ad.'],
        ]} />
      </Section>
    </GuideLayout>
  );
}
